// @ts-strict-ignore
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { NoStyleButton } from "@fullscript/aviary-web";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@radix-ui/react-collapsible";
import type { ReactNode } from "react";
import { useState } from "react";

import { Typography, Link, Avatar } from "@aviary";
import { useIsDS4Enabled } from "@aviary/hooks/useIsDS4Enabled";
import { emersonRoutes } from "@clinic/emerson/data/emersonRoutes";
import { l } from "@emerson/locales/i18n";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import * as styles from "./EmersonProfileToggle.styles";

interface Props {
  firstName: string;
  lastName: string;
  avatar?: string;
  avatarForText: string;
  signOutText: string;
  t: (key: string, options?: any) => string;
  children: ReactNode;
}

const EmersonProfileToggle = ({
  avatar,
  signOutText,
  avatarForText,
  firstName,
  lastName,
  t,
  children,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDS4Enabled = useIsDS4Enabled();

  const conditionalOpenStyles = styleObject => {
    return [styleObject.base, isOpen && styleObject.open, isDS4Enabled && styleObject.ds4];
  };

  return (
    <div css={conditionalOpenStyles(styles.accountProfile)}>
      <Collapsible css={styles.collapsible} onOpenChange={setIsOpen}>
        <CollapsibleTrigger css={styles.collapsibleTrigger} asChild>
          <NoStyleButton>
            <div
              tabIndex={0}
              aria-label={t(l.common.toggleAccountProfile)}
              css={conditionalOpenStyles(styles.profile)}
              data-e2e="sidebar-collapsible-settings"
            >
              <Avatar
                image={avatar}
                firstName={firstName}
                lastName={lastName}
                label={`${avatarForText} ${firstName} ${lastName}`}
                size="xsmall"
                isLight={!isDS4Enabled}
              />
              <Typography type="h5" css={conditionalOpenStyles(styles.name)}>
                {`${firstName} ${lastName}`}
              </Typography>
              <FontAwesomeIcon icon={!isOpen ? faChevronUp : faChevronDown} />
            </div>
          </NoStyleButton>
        </CollapsibleTrigger>
        <CollapsibleContent css={styles.collapsibleContent}>
          <div>{children}</div>
          <div css={styles.signOut}>
            <Link
              isColor="danger"
              isUnderLined={false}
              css={styles.signOutText}
              href={emersonRoutes.logout}
              target="_self"
              data-e2e="sidebar-signout"
            >
              {signOutText}
            </Link>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export { EmersonProfileToggle };
